import { ANALYTICS_EVENTS } from '@nandosaus/constants';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import { addThousandsSeparators } from '@nandosaus/state-management/lib/util/formatter';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { analytics } from '../../analytics';
import { Flex } from '../grid';
import { Icon } from '../icons';
import { Link } from '../link';
import { navigation } from '../../constants';
import { P } from '../typography/new';
import { setLandingPage } from '../../utils/auth';
import { ToggleSwitch } from '../input/toggle-switch';

const ChevronButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  transition: transform 0.2s ease-in-out;
  transform: rotate(${({ $isExpanded }) => ($isExpanded ? '180deg' : '0deg')});
`;

const PointsRedemptionToggleRow = observer(
  ({
    availablePointsDollars,
    pointsToggleChecked,
    onPointsToggleChange,
    pointsAmountDollars,
    showChevron,
    isExpanded,
    onChevronClick,
    readOnly,
  }) => {
    const { MemberStore, CartStore } = useRootStore();
    // Points payment can be blocked by a promo, free delivery, or a public-holiday surcharge.
    // When it is, hide the toggle and surface the reason inline instead (not while read-only editing).
    const pointsDisabledReason = readOnly ? null : CartStore.pointsPaymentDisabledReason;
    const isZeroBalance = parseFloat(availablePointsDollars ?? '0') <= 0;

    return (
      <Flex alignItems="center" gap="12px" width="100%">
        <Flex alignItems="center" gap="8px" flex="1">
          <Icon name="periPerksLogo" height="23px" width="30px" stroke="none" />
          {MemberStore.isSignedIn && pointsDisabledReason && (
            <P variant="standard-semi-bold">Sorry, {pointsDisabledReason}</P>
          )}
          {MemberStore.isSignedIn && !pointsDisabledReason && (
            <P variant="standard-semi-bold">
              {isZeroBalance && 'No points available to redeem'}
              {!isZeroBalance && pointsToggleChecked && pointsAmountDollars > 0 && (
                <>
                  <P as="span" variant="standard-bold" color="customerRewards3" fontSize="18px">
                    {`$${addThousandsSeparators(pointsAmountDollars.toFixed(2))}`}
                  </P>
                  {' in points applied. Well played.'}
                </>
              )}
              {!isZeroBalance && !(pointsToggleChecked && pointsAmountDollars > 0) && (
                <>
                  {'Use points to take off up to '}
                  <P as="span" variant="standard-bold" color="customerRewards3" fontSize="18px">
                    {`$${addThousandsSeparators(availablePointsDollars)}`}
                  </P>
                </>
              )}
            </P>
          )}
          {!MemberStore.isSignedIn && (
            <P variant="standard-semi-bold">
              <Link
                href={navigation.SIGN_IN}
                textDecoration="underline"
                variant="standard-semi-bold"
                onClick={() => {
                  analytics.track(ANALYTICS_EVENTS.USER_SIGN_IN_CLICKED, { location: 'Cart Points Banner' });
                  setLandingPage(navigation.MENU);
                }}
              >
                Sign in
              </Link>
              {' to redeem your PERi-Perks'}
            </P>
          )}
        </Flex>
        {MemberStore.isSignedIn && !readOnly && !pointsDisabledReason && (
          <ToggleSwitch
            name="peri-points-toggle"
            ariaLabel="Redeem PERi-Points"
            checked={isZeroBalance ? false : pointsToggleChecked}
            onChange={onPointsToggleChange}
            disabled={isZeroBalance}
            hasIcons
            hideUncheckedIcon
            onColor="#000000"
            offColor={isZeroBalance ? '#b9b8b4' : '#D6BFB8'}
            onHandleColor="#ffffff"
            offHandleColor={isZeroBalance ? '#ffffff' : '#E9E9E9'}
            checkedIconFill="#000000"
            trackHeight={28}
            trackWidth={52}
            handleDiameter={24}
            handleBoxShadow="none"
            handleActiveBoxShadow="none"
          />
        )}
        {MemberStore.isSignedIn && !readOnly && !pointsDisabledReason && showChevron && (
          <ChevronButton
            type="button"
            onClick={onChevronClick}
            $isExpanded={isExpanded}
            disabled={!onChevronClick || isZeroBalance}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <Icon name="chevron" height="24px" width="24px" fill="#000000" />
          </ChevronButton>
        )}
      </Flex>
    );
  }
);

PointsRedemptionToggleRow.propTypes = {
  availablePointsDollars: PropTypes.string,
  pointsToggleChecked: PropTypes.bool,
  onPointsToggleChange: PropTypes.func,
  pointsAmountDollars: PropTypes.number,
  showChevron: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onChevronClick: PropTypes.func,
  readOnly: PropTypes.bool,
};

PointsRedemptionToggleRow.defaultProps = {
  availablePointsDollars: null,
  pointsToggleChecked: false,
  onPointsToggleChange: () => {},
  pointsAmountDollars: null,
  showChevron: false,
  isExpanded: false,
  onChevronClick: null,
  readOnly: false,
};

export { PointsRedemptionToggleRow };
